import React, { useRef,useState, useEffect, forwardRef } from "react";
import { useLocation } from "react-router-dom"
import moment from "moment"
import { showToast } from "../../Shared/ToastMessage"
import axios from "axios"
import { localserver } from "../../../api/common"

const EventInvoiceMobile = () => {
  const useQuery = () => {
    return new URLSearchParams(useLocation().search)
  }
  const query = useQuery()
  const eventId = query.get("eventId")
  const email = query.get("email")
  const invoiceRef = useRef(null)
  const [eventFullDetails, setEventFullDetails] = useState({})
  const { data, payments, event, event_configuration, tickets, state_is_same } = eventFullDetails || {}
  const eventDetails = data?.[0] || {}
  const paymentDetails = payments?.[0] || {}
  const autoDownload = true
  const [getEventStatus, setGetEventStatus] = useState(false)

  // Parse form_data
  const parseFormData = (formData) => {
    try {
      const parsed = JSON.parse(formData || "[]")
      return parsed[0]?.custom_fields || []
    } catch (error) {
      console.error("Error parsing form_data:", error)
      return []
    }
  }

  const formFields = parseFormData(eventDetails.form_data)
  const firstName = formFields.find((f) => f.label === "First Name")?.value || eventDetails.first_name || "N/A"
  const lastName = formFields.find((f) => f.label === "Last Name")?.value || eventDetails.last_name || ""
  const attendeeEmail = formFields.find((f) => f.label === "Email Address")?.value || eventDetails.email || "N/A"

  const parseTicketData = (ticketData) => {
    try {
      return JSON.parse(ticketData || "{}")
    } catch (error) {
      console.error("Error parsing ticket_data:", error)
      return {}
    }
  }

  const parseAddOnsData = (addOnsData) => {
    try {
      return JSON.parse(addOnsData || "[]")
    } catch (error) {
      console.error("Error parsing add_ons_data:", error)
      return []
    }
  }

  const ticketData = parseTicketData(eventDetails.ticket_data)
  const addOnsData = parseAddOnsData(eventDetails.add_ons_data)

  const getSelectedAddOns = () => {
    try {
      const formData = JSON.parse(eventDetails.form_data || "[]")
      const addOns = formData[0]?.add_ons || []
      return addOns
    } catch (error) {
      console.error("Error parsing selected add-ons:", error)
      return []
    }
  }

  const selectedAddOns = getSelectedAddOns()

  const getTaxBreakdown = (taxOptions, baseAmount, taxType) => {
    if (!taxOptions || taxOptions.length === 0) return { taxes: [], totalTax: 0, taxLabel: "No Tax" }

    const taxes = []
    let totalTax = 0

    if (state_is_same==="yes") {
      // Same state: Use CGST + SGST
      const cgst = taxOptions.find((tax) => tax.tax_name === "CGST")
      const sgst = taxOptions.find((tax) => tax.tax_name === "SGST")

      if (cgst && sgst) {
        const cgstAmount =
          taxType === "Excluding Tax"
            ? (baseAmount * cgst.tax_percentage) / 100
            : (baseAmount * cgst.tax_percentage) / (100 + cgst.tax_percentage + sgst.tax_percentage)
        const sgstAmount =
          taxType === "Excluding Tax"
            ? (baseAmount * sgst.tax_percentage) / 100
            : (baseAmount * sgst.tax_percentage) / (100 + cgst.tax_percentage + sgst.tax_percentage)

        taxes.push({ name: "CGST", percentage: cgst.tax_percentage, amount: cgstAmount })
        taxes.push({ name: "SGST", percentage: sgst.tax_percentage, amount: sgstAmount })
        totalTax = cgstAmount + sgstAmount
      }
    } else {
      // Different state: Use IGST
      const igst = taxOptions.find((tax) => tax.tax_name === "IGST")
      if (igst) {
        const igstAmount =
          taxType === "Excluding Tax"
            ? (baseAmount * igst.tax_percentage) / 100
            : (baseAmount * igst.tax_percentage) / (100 + igst.tax_percentage)

        taxes.push({ name: "IGST", percentage: igst.tax_percentage, amount: igstAmount })
        totalTax = igstAmount
      }
    }

    const taxLabel = taxes.map((tax) => `${tax.name} ${tax.percentage}%`).join(" + ")
    return { taxes, totalTax, taxLabel: `(${taxLabel})` }
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return moment(dateString).format("DD MMM YYYY")
  }

  const ticketBaseAmount = Number.parseFloat(paymentDetails.total_ticket_amount || 0)
  const ticketTaxAmount = Number.parseFloat(paymentDetails.total_tax_amount || 0)
  const addonsAmount = Number.parseFloat(paymentDetails.total_addons_amount || 0)
  const totalAmount = Number.parseFloat(paymentDetails.total_amount || 0)
  const currency = paymentDetails.currency || "INR"
  const couponAmount = parseFloat(paymentDetails.coupon_applied_amount || 0)
  const couponPercentage = paymentDetails.coupon_applied_percentage || "0"
  const subtotal = ticketBaseAmount + addonsAmount
  const discountedSubtotal = subtotal - couponAmount
  const totalTaxAmount = parseFloat(paymentDetails.total_tax_amount || 0)

  // Get tax breakdown for main ticket
  const ticketTaxBreakdown = getTaxBreakdown(ticketData.tax_options, ticketBaseAmount, ticketData.tax_type)

  const handleDownloadPDF = () => {
    // Create a new window with the invoice content
    const printWindow = window.open("", "_blank")

    if (printWindow) {
      const invoiceContent = document.getElementById("invoice-content")?.innerHTML

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body { 
                margin: 0; 
                padding: 0;
                font-size: 12px;
                line-height: 1.3;
              }
              
              .no-print { 
                display: none !important; 
              }
              
              .print-container {
                max-width: 100%;
                margin: 0;
                padding: 0;
              }
              
              .print-header {
                padding: 16px !important;
                margin-bottom: 12px !important;
              }
              
              .print-content {
                padding: 16px !important;
              }
              
              .print-section {
                margin-bottom: 16px !important;
                page-break-inside: avoid;
              }
              
              .print-grid {
                display: grid !important;
                grid-template-columns: 1fr 1fr !important;
                gap: 16px !important;
                margin-bottom: 16px !important;
              }
              
              .print-table {
                font-size: 11px !important;
                margin-bottom: 16px !important;
              }
              
              .print-table th,
              .print-table td {
                padding: 8px 12px !important;
              }
              
              .print-summary {
                width: 300px !important;
                margin-left: auto !important;
                margin-bottom: 16px !important;
              }
              
              .print-payment {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 12px !important;
              }
              
              .shadow-xl, .shadow-lg, .shadow-sm { 
                box-shadow: none !important; 
              }
              
              .rounded-lg, .rounded-xl {
                border-radius: 4px !important;
              }
              
              /* Ensure page breaks */
              .page-break-before {
                page-break-before: always;
              }
              
              .page-break-after {
                page-break-after: always;
              }
              
              .avoid-break {
                page-break-inside: avoid;
              }
            }
            
            body { 
              font-family: system-ui, -apple-system, sans-serif; 
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${invoiceContent}
          </div>
        </body>
        </html>
      `)

      printWindow.document.close()

      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 1000)
    }
  }

  const getEventDetailsByUser = async () => {
    try {
      setGetEventStatus(true)
      const response = await axios.post(
        `${localserver}/get_register_ticket_ByUser`,
        {
          email: email,
          event_id: eventId,
          attendee_id: "",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      if (response.data.success) {
        console.log("API Response:", response.data)
        setEventFullDetails(response.data || [])
      } else {
        console.error("Error: Failed to fetch user data")
        showToast("Failed to fetch events", "error")
      }
    } catch (error) {
      console.error("Error fetching events:", error)
      showToast("An error occurred while fetching events", "error")
    } finally {
      setGetEventStatus(false)
      if (autoDownload) {
        console.log("Auto-download triggered")
        handleDownloadPDF()
      }
    }
  }

  useEffect(() => {
    getEventDetailsByUser()
  }, [email])

  return (
    <>
      {getEventStatus ? (
        <div className="text-center p-5">
          <i className="fa fa-spinner fa-spin fa-3x"></i>
          <p className="mt-3">Loading event details...</p>
        </div>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
          <div className="max-w-4xl mx-auto">
            <div
              id="invoice-content"
              ref={invoiceRef}
              className="bg-white shadow-xl rounded-lg overflow-hidden print-container"
              style={autoDownload ? { position: "absolute", left: "-9999px" } : {}}
            >
              <div
                className="px-8 py-6 print-header avoid-break"
                style={{ background: "linear-gradient(135deg, #184778 0%, #4964aa 100%)" }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white p-2 rounded-lg shadow-md">
                      <img
                        src={event?.event_logo || "/amasicon-logo.png"}
                        alt="Event Logo"
                        className="w-16 h-12 object-contain"
                        onError={(e) => {
                          e.target.src = "/amasicon-logo.png"
                        }}
                      />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-white">{event_configuration?.organization_name || ""}</h1>
                      <p className="text-blue-100 text-xs">
                        {event?.event_type || ""} • {event?.venue_city || ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/30">
                      <h2 className="text-2xl font-bold text-white mb-1">RECEIPT</h2>
                      <div className="text-blue-100 text-xs">Payment Confirmation</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 print-content">
                <div className="grid grid-cols-2 gap-6 mb-6 print-grid print-section avoid-break">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                      <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                        Address
                      </h3>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border-l-4" style={{ borderColor: "#31719c" }}>
                      <div className="text-xs text-black leading-relaxed space-y-1">
                        <div className="text-sm font-bold" style={{ color: "#184778" }}>
                          {event_configuration?.organization_name || "AMASICON 2025"}
                        </div>
                        <div>{event_configuration?.address_line1 || ""} {event_configuration?.address_line2 || ""}</div>
                        <div>{event_configuration?.city || ""}, {event_configuration?.state_name || ""}</div>
                        <div>{event_configuration?.country_name || ""}, {event_configuration?.postcode || ""}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                      <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                        Receipt Details
                      </h3>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                            Receipt#
                          </span>
                          <span className="text-xs font-bold text-black">
                            {`${event?.event_name || "Event"}-${eventDetails.reg_code || ""}`}
                          </span>
                        </div>
                      </div>
                      <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                            Date of Issue
                          </span>
                          <span className="text-xs font-bold text-black">
                            {formatDate(paymentDetails.payment_date || paymentDetails.created_at) || ""}
                          </span>
                        </div>
                      </div>
                      <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                            Order ID
                          </span>
                          <span className="text-xs font-bold text-black">{paymentDetails.id || ""}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-6 print-section avoid-break">
                  <div className="flex items-center mb-3">
                    <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                    <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                      BILLED TO
                    </h3>
                  </div>
                  <div
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded border"
                    style={{ borderColor: "#31719c" }}
                  >
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                        <span className="font-medium w-28" style={{ color: "#4964aa" }}>
                          Customer Name
                        </span>
                        <span className="text-black font-bold">{`${firstName} ${lastName}`.trim() || "N/A"}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                        <span className="font-medium w-28" style={{ color: "#4964aa" }}>
                          Customer Email
                        </span>
                        <span className="text-black">{attendeeEmail || "N/A"}</span>
                      </div>
                      {/* <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                        <span className="font-medium w-28" style={{ color: "#4964aa" }}>
                          Customer Address
                        </span>
                        <span className="text-black">
                          {formFields.find((f) => f.label.toLowerCase().includes("address"))?.value || "N/A"}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>
                <div className="mb-6 print-section avoid-break">
                  <div className="flex items-center mb-3">
                    <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                    <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                      Order Details
                    </h3>
                  </div>
                  <div className="rounded border overflow-hidden print-table" style={{ borderColor: "#31719c" }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr style={{ background: "linear-gradient(135deg, #184778 0%, #4964aa 100%)" }}>
                          <th className="text-left py-3 px-4 font-bold text-white text-xs">Item</th>
                          <th className="text-center py-3 px-4 font-bold text-white text-xs">QTY</th>
                          <th className="text-right py-3 px-4 font-bold text-white text-xs">Base Price</th>
                          <th className="text-right py-3 px-4 font-bold text-white text-xs">Tax</th>
                          <th className="text-right py-3 px-4 font-bold text-white text-xs">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-white">
                          <td className="py-3 px-4 text-xs text-black border-b" style={{ borderColor: "#31719c" }}>
                            <div className="flex items-center">
                              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                              <div>
                                <div className="font-medium">{ticketData.title || "Ticket"}</div>
                                <div className="text-gray-500 text-xs">
                                  {ticketData.tax_type} {ticketTaxBreakdown.taxLabel}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center border-b" style={{ borderColor: "#31719c" }}>
                            <span
                              className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: "#4964aa" }}
                            >
                              {paymentDetails.ticket_count || 1}
                            </span>
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {ticketBaseAmount.toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {ticketTaxAmount.toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-bold text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {(ticketBaseAmount + ticketTaxAmount).toFixed(2)}
                          </td>
                        </tr>

                        {selectedAddOns.map((selectedAddon, index) => {
                          const addonData = addOnsData.find((addon) => addon.id === selectedAddon.addon_id)
                          if (!addonData) return null

                          const variant = addonData.variant_options?.find(
                            (v) => v.variant_name === selectedAddon.variant_name,
                          )
                          const variantPrice = variant ? parseFloat(variant.variant_price) || 0 : 0
                          const quantity = parseInt(selectedAddon.quantity) || 1
                          const basePrice = variantPrice * quantity

                          const addonTaxBreakdown = getTaxBreakdown(
                            addonData.tax_options,
                            basePrice,
                            addonData.tax_type,
                          )

                          const taxAmount = addonTaxBreakdown.totalTax

                          const subtotal = addonData.tax_type === "Including Tax" ? basePrice : basePrice + taxAmount

                          return (
                            <tr key={index} className="bg-white">
                              <td className="py-3 px-4 text-xs text-black border-b" style={{ borderColor: "#31719c" }}>
                                <div className="flex items-center">
                                  <div
                                    className="w-2 h-2 rounded-full mr-2"
                                    style={{ backgroundColor: "#31719c" }}
                                  ></div>
                                  <div>
                                    <div className="font-medium">
                                      {addonData.add_on_name} - {selectedAddon.variant_name}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      {addonData.tax_type} {addonTaxBreakdown.taxLabel}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center border-b" style={{ borderColor: "#31719c" }}>
                                <span
                                  className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: "#4964aa" }}
                                >
                                  {quantity}
                                </span>
                              </td>
                              <td
                                className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                                style={{ borderColor: "#31719c" }}
                              >
                                {currency}{" "}
                                {(addonData.tax_type === "Including Tax" ? (basePrice - taxAmount) : basePrice).toFixed(
                                  2,
                                )}
                              </td>
                              <td
                                className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                                style={{ borderColor: "#31719c" }}
                              >
                                {currency} {taxAmount.toFixed(2)}
                              </td>
                              <td
                                className="py-3 px-4 text-right text-xs font-bold text-black border-b"
                                style={{ borderColor: "#31719c" }}
                              >
                                {currency} {subtotal.toFixed(2)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mb-6 print-section avoid-break">
                  <div className="flex justify-end">
                    <div className="w-80 print-summary">
                      <div
                        className="bg-gradient-to-br from-gray-50 to-blue-50 rounded border overflow-hidden"
                        style={{ borderColor: "#31719c" }}
                      >
                        <div className="p-4 space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="font-medium text-black">Tickets</span>
                            <span className="font-bold text-black">
                              {currency} {ticketBaseAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="font-medium text-black">Add-ons</span>
                            <span className="font-bold text-black">
                              {currency} {addonsAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs font-medium text-black">
                            <span>Subtotal</span>
                            <span className="font-bold">
                              {currency} {subtotal.toFixed(2)}
                            </span>
                          </div>
                          {couponAmount > 0 && (
                            <>
                              <div className="flex justify-between text-xs text-red-600">
                                <span className="font-medium">Discount ({couponPercentage}%)</span>
                                <span className="font-bold">-{currency} {couponAmount.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-black">Subtotal after discount</span>
                                <span className="font-bold text-black">
                                  {currency} {discountedSubtotal.toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                          {ticketTaxBreakdown.taxes.map((tax, index) => (
                            <div key={index} className="flex justify-between text-xs">
                              <span className="font-medium text-black">
                                {tax.name} @ {tax.percentage}%
                              </span>
                              <span className="font-bold text-black">
                                {currency} {tax.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {ticketTaxBreakdown.taxes.length > 0 && (
                            <div className="flex justify-between text-xs font-medium text-black">
                              <span>Total Tax {ticketTaxBreakdown.taxLabel}</span>
                              <span className="font-bold">
                                {currency} {totalTaxAmount.toFixed(2)}
                              </span>
                            </div>
                          )}
                          <div className="border-t-2 pt-2 mt-3" style={{ borderColor: "#31719c" }}>
                            <div
                              className="flex justify-between items-center p-2 rounded"
                              style={{ backgroundColor: "#184778" }}
                            >
                              <span className="font-bold text-white text-sm">Total Amount</span>
                              <span className="font-bold text-white text-lg">
                                {currency} {totalAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center pt-4 border-t" style={{ borderColor: "#31719c" }}>
                  <div className="text-xs" style={{ color: "#4964aa" }}>
                    <div className="font-medium">Powered by</div>
                    <div className="mt-1 text-gray-400">This is a digitally generated receipt</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EventInvoiceMobile